import { ErrorBoundary } from "@datadog/browser-rum-react";
import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { RedoIcon } from "@redotech/arbiter/icon/redo-icon";
import { Header, Text } from "@redotech/arbiter/text";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import { type ReactNode, memo } from "react";

import * as css from "./page-error-boundary.module.css";

export const PageErrorBoundary = memo(function PageErrorBoundary({
  children,
}: {
  children: ReactNode;
}) {
  return <ErrorBoundary fallback={PageErrorFallback}>{children}</ErrorBoundary>;
});

function PageErrorFallback({ resetError }: { resetError: () => void }) {
  return (
    <Flex
      align="flex-start"
      bgColor="primary"
      borderColor="secondary"
      borderRadius="md"
      borderStyle="solid"
      borderWidth="1px"
      className={css.fallback}
      dir="column"
      gap="lg"
      maxW="md"
      mx="auto"
      my="8xl"
      p="4xl"
      role="alert"
    >
      <Flex
        align="center"
        bgColor="warning-secondary"
        borderRadius="full"
        className={css.icon}
        justify="center"
      >
        <RedoIcon Icon={AlertTriangleIcon} size={20} />
      </Flex>
      <Header as="h1" fontSize="xxs" fontWeight="semibold" m="none">
        Something went wrong on this page
      </Header>
      <Text fontSize="sm" m="none" textColor="tertiary">
        Try reloading the page. If this keeps happening, let us know.
      </Text>
      <RedoButton
        hierarchy="primary"
        onClick={resetError}
        size="sm"
        text="Try again"
      />
    </Flex>
  );
}
